import React, { useEffect, useMemo, useRef } from 'react';
import { Card, Form, Typography, Button, Spin, message } from 'antd';
import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import HotelProfileForm, { HotelProfileFormValues } from './HotelProfileForm';
import { RootState } from '@/AppRedux/reducers';
import {
  getHotelListProcessClear,
  getHotelProfileRequest,
  updateHotelProfileRequest,
} from '@/AppRedux/actions/admin/hotelList';

const { Title, Text } = Typography;

const EditHotelProfile: React.FC = () => {
  const intl = useIntl();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { hotelId } = useParams<{ hotelId: string }>();

  const { hotelProfileData, loading, process } = useSelector((state: RootState) => state.hotelList);
  const useCurrentFirstRender = useRef(true)

  const normalizedHotelId = useMemo(() => {
    return hotelId ? decodeURIComponent(hotelId) : '';
  }, [hotelId]);

  useEffect(() => {
    if (!normalizedHotelId) {
      message.error('Invalid hotel id.');
      navigate(-1);
      return;
    }
    if (useCurrentFirstRender.current) {
      dispatch(getHotelProfileRequest(normalizedHotelId));
      useCurrentFirstRender.current = false
    }
  }, [dispatch, navigate, normalizedHotelId, useCurrentFirstRender]);

  useEffect(() => {
    if (hotelProfileData) {
      form.setFieldsValue({
        code: hotelProfileData.code,
        name: hotelProfileData.name,
        active: hotelProfileData.active,
      });
    }
  }, [form, hotelProfileData]);

  useEffect(() => {
    if (process.action === "GET_HOTEL_PROFILE_REQUEST" && process.message && !process.status) {
      message.error(process.message);
      dispatch(getHotelListProcessClear());
      navigate(-1);
      return;
    }

    if (process.action === "UPDATE_HOTEL_PROFILE_REQUEST" && process.message) {
      if (process.status) {
        message.success(process.message);
        dispatch(getHotelListProcessClear());
        navigate(-1);
      } else {
        message.error(process.message);
        dispatch(getHotelListProcessClear());
      }
    }
  }, [dispatch, navigate, process]);

  const onFinish = (values: HotelProfileFormValues) => {
    if (!normalizedHotelId) {
      message.error('Invalid hotel id.');
      return;
    }

    dispatch(updateHotelProfileRequest({
      id: normalizedHotelId,
      code: String(form.getFieldValue('code') || values.code || '').trim(),
      name: values.name?.trim(),
      active: Boolean(values.active),
    }));
  };

  const onBack = () => {
    navigate(-1);
  };

  const loadingHotelProfile =
    loading &&
    process.action === "GET_HOTEL_PROFILE_REQUEST" &&
    !hotelProfileData;

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto' }}>
      <Card style={{ borderRadius: 8, boxShadow: '0 1px 2px 0 rgba(0,0,0,0.03)' }}>
        <div style={{ marginBottom: 32 }}>
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={onBack}
            style={{ marginBottom: 16 }}
          >
            {intl.formatMessage({ id: 'admin.hotel_profile.back_to_list', defaultMessage: 'Back to Hotel List' })}
          </Button>
          <Title level={4} style={{ margin: 0 }}>
            {intl.formatMessage({ id: 'admin.hotel_profile.edit.title', defaultMessage: 'Edit Hotel Profile' })}
          </Title>
          <Text type="secondary">
            {intl.formatMessage({ id: 'admin.hotel_profile.edit.description', defaultMessage: 'Edit the details of the hotel' })}
          </Text>
        </div>

        <Spin spinning={loadingHotelProfile}>
          <HotelProfileForm
            form={form}
            onFinish={onFinish}
            initialValues={{}}
            isEdit={true}
            submitLoading={loading && process.action === "UPDATE_HOTEL_PROFILE_REQUEST"}
          />
        </Spin>
      </Card>
    </div>
  );
};

export default EditHotelProfile;

